import * as React from 'react';
import { t } from '@lingui/core/macro';
import Button from 'components/Button';
import { tenantConfig } from 'utils/tenant';
import { myCameraStream } from 'state/video';
import { voipRtcConnection } from 'utils/ui/rtc';
import { classNames } from 'utils/ui/css';
import { isVoipEnabled } from 'state/session';

type TSessionControls = {
  voipActive?: boolean;
  children?: React.ReactNode;
};

const SessionControls: React.FC<TSessionControls> = ({ voipActive = false, children }) => {
  /** This component can be rendered as a part of the VOIP WC and as a child of it,
   * it would have these hooks invoked at the parent level already */
  if (!voipActive) {
    tenantConfig().useState();
    myCameraStream().useState();
  }

  const canRenderVoipButtons =
    voipActive ||
    (tenantConfig().isAllowed('voip') && myCameraStream().stream instanceof MediaStream && isVoipEnabled());

  const hasLocalStream = myCameraStream().stream instanceof MediaStream;

  return (
    <>
      {/* VOIP has to be enabled and stream accessible/allowed */}
      {canRenderVoipButtons && (
        <>
          <Button
            icon="ex3d-icon--camera"
            part="voip-button--camera"
            data-testid="voip-button-camera"
            svgClassName="bg-black"
            ariaLabel={myCameraStream().get('isVideoEnabled') ? t`Turn camera off` : t`Turn camera on`}
            className={classNames(
              'stream-header-icon-camera',
              myCameraStream().get('isVideoEnabled')
                ? 'ex3d-enabled ex3d-icon--state__on'
                : 'ex3d-disabled stream-header-icon-camera-disabled ex3d-icon--state__off',
              myCameraStream().get('isVideoAllowed') ? '' : 'hidden'
            )}
            disabled={!hasLocalStream}
            onClick={() => {
              voipRtcConnection.toggleMedia('video');
            }}
          />
          <Button
            icon="ex3d-icon--mic"
            part="voip-button--mic"
            data-testid="voip-button-mic"
            svgClassName="bg-black"
            ariaLabel={myCameraStream().get('isAudioEnabled') ? t`Mute microphone` : t`Unmute microphone`}
            className={classNames(
              'stream-header-icon-mic',
              myCameraStream().get('isAudioEnabled')
                ? 'ex3d-enabled ex3d-icon--state__on'
                : 'ex3d-disabled stream-header-icon-mic-disabled ex3d-icon--state__off',
              myCameraStream().get('isAudioAllowed') ? '' : 'hidden'
            )}
            disabled={!hasLocalStream}
            onClick={() => {
              voipRtcConnection.toggleMedia('audio');
            }}
          />
        </>
      )}
      {children}
    </>
  );
};

export default SessionControls;
